import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { concatMap, takeUntil } from 'rxjs';
import { IFundProfileModel } from '../models/fund-profile.model';
import { IFundChart, IFundPortfolioChart, IFundPortfolioGoldChart } from '../models/fund-chart.model';
import { FundProfileInfoCardComponent } from './components/fund-profile-info-card/fund-profile-info-card.component';
import { FundProfilePriceCardComponent } from './components/fund-profile-price-card/fund-profile-price-card.component';
import { FundProfileUserAssetsComponent } from './components/fund-profile-user-assets/fund-profile-user-assets.component';
import { FundProfileGeneralInfoComponent } from './components/fund-profile-general-info/fund-profile-general-info.component';
import { FundProfileFooterComponent } from './components/fund-profile-footer/fund-profile-footer.component';
import { AssetsCompositionChartComponent } from './components/assets-composition-chart/assets-composition-chart.component';
import { NavChartComponent } from './components/nav-chart/nav-chart.component';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { FundDataService } from '../../../components/core/services/fund-data.service';
import { BaseComponent } from '../../../components/core/components/base/base.component';
import { IPortfolioDetail } from '../../../components/core/models/user-fund-assets.interface';
import { ErrorService } from '../../../components/core/services/error.service';
import { InstrumentService } from '../../../components/core/services/instrument.service';
import { WEALTH_TOKEN } from '../../../components/utils/variables';
import { EIntrackEventName } from '../../../components/core/models/intrack-event-name.enum';
import { OFF_TIME_ERROR_ROUTE, PROVIDER_CAPACITY_FULL_ROUTE, PURCHASE_ROUTE, SELL_ROUTE } from '../../../data-access/constants/app-routes';
import { InstrumentOfftimeReason } from '../../../data-access/enums/instrument-offtime-reason';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { CollateralTouchPointComponent } from '../../collateral/components/collateral-touch-point/collateral-touch-point.component';
import { CollateralService } from '../../collateral/services/collateral.service';
import { ICollateralResult } from '../../collateral/models/collateral-status-result.interface';
import { ECollateralRequestStatus } from '../../collateral/models/collateral-request-status.enum';
import { ProfileService } from '../../../components/core/services/profile.service';
import { UserInfoModel } from '../../user-profile/models/user-info.model';
import { GolrangBottomSheetComponent } from '../funds-list/components/golrang-bottom-sheet/golrang-bottom-sheet.component';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { NgClass } from '@angular/common';
import { NgxSegmentedControlComponent } from '@digipay/ngx-segmented-control';
import { SegmentItemsModel } from '@digipay/ngx-segmented-control/lib/models/types';
import { IButton } from '../../ipo/models/ipo-buttons.interface';

@Component({
  selector: 'app-fund-profile',
  standalone: true,
  imports: [
    FundProfileFooterComponent,
    FundProfileInfoCardComponent,
    FundProfilePriceCardComponent,
    FundProfileUserAssetsComponent,
    FundProfileGeneralInfoComponent,
    AssetsCompositionChartComponent,
    NavChartComponent,
    NgxButtonComponent,
    CollateralTouchPointComponent,
    SpinnerComponent,
    NgClass,
    NgxSegmentedControlComponent,
  ],
  templateUrl: './fund-profile.component.html',
  styleUrl: './fund-profile.component.scss',
})
export class FundProfileComponent extends BaseComponent implements OnInit, OnDestroy {
  symbol = signal<string | undefined>(undefined);
  profile = signal<IFundProfileModel | undefined>(undefined);

  navChart = signal<IFundChart[]>([]);
  compositionChart = signal<(IFundPortfolioChart & Partial<IFundPortfolioGoldChart>)[]>([]);
  chartTab = signal<boolean>(true);
  userAsset = signal<IPortfolioDetail | undefined>(undefined);

  isLoading = signal<boolean>(true);

  buttons = signal<IButton[]>([
    {
      label: 'سرمایه‌گذاری',
      style: 'fill',
      isActive: false,
      id: 'buy',
    },
  ]);
  qParams = signal<Params | undefined>(undefined);
  collateralResult = signal<ICollateralResult | undefined>(undefined);
  collateralTitle = signal<string | undefined>(undefined);
  collateralDescription = signal<string | undefined>(undefined);
  user = signal<UserInfoModel | undefined>(undefined);
  options = signal<SegmentItemsModel[]>([]);

  protected readonly ECollateralRequestStatus = ECollateralRequestStatus;
  private bottomSheet = inject(NgxBottomSheetService);
  private navigationService = inject(WealthNavigationService);
  private backHandler = inject(BackHandlerService);
  private activatedRoute = inject(ActivatedRoute);
  private fundDataService = inject(FundDataService);
  private errorService = inject(ErrorService);
  private instrumentService = inject(InstrumentService);
  private eventService = inject(NgxEventTrackerService);
  private collateralService = inject(CollateralService);
  private profileService = inject(ProfileService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.options.set([
      { text: 'نمودارها', id: 'Charts', value: 'CHARTS', disable: false },
      { text: 'اطلاعات کلی', id: 'Info', value: 'GENERAL_INFO', disable: false },
    ]);

    this.qParams.set(this.activatedRoute.snapshot.queryParams);
    this.profileService.getProfile().subscribe((res) => {
      if (res?.success) {
        this.user.set(res.result);
        const wealthToken = JSON.parse(localStorage.getItem(WEALTH_TOKEN))?.accessToken;
        if (!wealthToken) return;
        this.activatedRoute.params
          .pipe(
            concatMap((params) => {
              this.symbol.set(params['id']);
              this.eventService.sendEvent({
                eventName: EIntrackEventName.FUND_PROFILE_DETAIL,
                eventData: {
                  FundId: this.symbol(),
                },
              });
              return this.fundDataService.getFundBySymbol(this.symbol());
            }),
            concatMap((fundProfile) => {
              if (fundProfile?.success) {
                this.profile.set(fundProfile.result);
                if (this.profile()?.symbol === '11421' && localStorage.getItem('golrang-rename-seen') !== 'true')
                  this.showGolrangBottomSheet();
                if (
                  (this.profile()?.investmentType === 'ETF' &&
                    this.profile()?.symbol !== 'IRT1SKDF0001' &&
                    this.profile()?.symbol != 'IRT1VBAZ0001') ||
                  (this.profile()?.investmentType == 'Mutual' && this.symbol() === '11421')
                ) {
                  this.buttons().find((x) => x.id == 'buy').isActive = true;
                }
              }
              return this.fundDataService.getChart(this.symbol());
            }),
            concatMap((fundChart) => {
              if (fundChart?.success) {
                this.navChart.set(fundChart.result.details);
                this.compositionChart.set(fundChart.result.portfolioDetails);
              }
              return this.fundDataService.getUserAssets();
            }),
          )
          .subscribe((userAssets) => {
            let asset = userAssets?.portfolioDetails.find((asset) => asset.symbol === this.profile()?.symbol);
            if (!asset) {
              asset = {
                investmentType: this.profile()?.investmentType,
                lastUpdatedAt: '',
                price: 0,
                quantity: 0,
                symbol: this.profile()?.symbol,
                title: this.profile()?.title,
                type: this.profile()?.type,
              };
            } else {
              if (asset.symbol === '11997' || asset?.symbol === 'IRT1SKDF0001') this.buttons().pop();
              if (asset?.quantity > 0) {
                const buyButton = this.buttons().find((x) => x.id == 'buy');
                if (buyButton) buyButton.isActive = true;
                this.buttons().unshift({
                  label: 'فروش',
                  style: 'tinted-on-elevated',
                  isActive: true,
                  id: 'sell',
                });
              }
            }

            this.userAsset.set(asset);
            this.isLoading.set(false);
          });

        if (this.qParams()['referrer']) {
          this.backHandler.setCustomBackUrl('hub');
        }

        this.collateralService.launch(this.symbol()).subscribe((res) => {
          if (res?.success) {
            this.collateralResult.set(res.result);
            if (res.result.status != ECollateralRequestStatus.None) {
              switch (res.result.status) {
                case ECollateralRequestStatus.Init:
                  this.collateralTitle.set('واحد‌ها رو وثیقه کن، اعتبار ۴ قسطه بگیر!');
                  this.collateralDescription.set('با وثیقه کردن واحد‌های این صندوق، تا سقف ۵۰ میلیون تومان اعتبار ۴ قسطه دریافت کنید.');
                  break;
                case ECollateralRequestStatus.Approved:
                  this.collateralTitle.set(`${res.result.units} واحد از دارایی شما وثیقه شده است`);
                  this.collateralDescription.set(
                    'شما با وثیقه کردن واحد‌های خود اعتبار ۴ قسطه دریافت کرده‌اید. تا پایان بازپرداخت اقساط، امکان آزاد سازی این واحد‌ها را ندارید.',
                  );
                  break;
                case ECollateralRequestStatus.Pending:
                  this.collateralTitle.set('درخواست وثیقه شدن واحد‌های شما در حال بررسی است...');
                  this.collateralDescription.set('بعد از ۱ تا ۲ روز کاری، نتیجه درخواست را از طریق پیامک به شما اطلاع می‌دهیم.');
                  break;
                case ECollateralRequestStatus.Rejected:
                  this.collateralTitle.set('درخواست شما برای وثیقه کردن واحد‌های این صندوق رد شد');
                  break;
              }
            }
          }
        });
      }
    });
  }

  override ngOnDestroy() {
    this.backHandler.setCustomBackUrl(undefined);
  }

  showGolrangBottomSheet() {
    localStorage.setItem('golrang-rename-seen', 'true');
    this.bottomSheet.openBottomSheet(GolrangBottomSheetComponent, {});
  }

  handleActiveSection(buttonLabel: SegmentItemsModel) {
    switch (buttonLabel.id) {
      case 'Info':
        this.chartTab.set(false);
        break;
      case 'Charts':
        this.chartTab.set(true);
        break;
    }
  }

  action(type: string) {
    this.instrumentService
      .isAvailable(this.profile()?.symbol, type)
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res.result?.isAvailable) {
          if (type === 'SELL') {
            this.navigationService.navigateWithState([SELL_ROUTE, this.profile()?.symbol], {
              state: {
                referrer: this.qParams()['referrer'],
              },
            });
          } else {
            this.eventService.sendEvent({
              eventName: EIntrackEventName.PROFILE_INVESTMENT_SELECT,
              eventData: {
                FundId: this.profile().symbol,
              },
            });
            this.navigationService.navigateWithState([PURCHASE_ROUTE, this.profile()?.symbol], {
              state: {
                referrer: this.qParams()['referrer'],
                backTo: 'profile',
              },
            });
          }
        } else {
          if (res.result.unavailabilityReason == InstrumentOfftimeReason.NOTBUYABLE) {
            this.navigationService.navigate([PROVIDER_CAPACITY_FULL_ROUTE]);
          } else if (res.result.unavailabilityReason == InstrumentOfftimeReason.OFFTIME) {
            this.errorService.setParams({
              description: res.result.noticeMessage,
              title: res.result.noticeTitle,
            });
            this.navigationService.navigate([OFF_TIME_ERROR_ROUTE]);
          }
        }
      });
  }
}
